import * as THREE from 'three';
import { SketchRelationKind } from '../../../../sketch/solver/constraint/relation-compatibility';
import { SingleTargetRelation } from '../../../../sketch/solver/constraint/relationDefinition/SingleTargetRelation';
import type { SketchArcObject } from '../../../../mesh/sketch/SketchArcObject';
import { resolvePreferredEdgeLabel } from '../../model/EdgeLabelUtils';
import type { ReferenceDrivenArcOnCurveConstraintOptions } from '../../../../../types/reference/sketch-entity-reference/ReferenceDrivenArcOnCurveConstraint';

export type { ReferenceDrivenArcOnCurveConstraintOptions } from '../../../../../types/reference/sketch-entity-reference/ReferenceDrivenArcOnCurveConstraint';

/**
 * 参考驱动圆弧在曲线上关系定义。
 *
 * 该对象只保存外部圆的稳定投影载荷，不直接修改目标几何；统一参数化编译器负责把
 * 圆心、半径与圆弧端点转换为方程，并由一次事务求解统一回写。
 */
export class ReferenceDrivenArcOnCurveConstraint extends SingleTargetRelation<SketchArcObject> {
	/** 引用记录稳定标识。 */
	public readonly referenceId: string;
	/** 关系面板使用的关系类型。 */
	public relationType = SketchRelationKind.OnEdge;

	/** 投影基础圆的世界坐标圆心。 */
	private readonly projectedCenterWorld: THREE.Vector3;
	/** 投影基础圆的半径。 */
	private readonly projectedRadius: number;
	/** 关系面板显示的来源边标签。 */
	private readonly edgeLabel: string;

	/** @param options 外部圆弧投影参数。 */
	public constructor(options: ReferenceDrivenArcOnCurveConstraintOptions) {
		super();
		this.referenceId = options.referenceId;
		this.projectedCenterWorld = options.projectedCenterWorld.clone();
		this.projectedRadius = options.projectedRadius;
		this.edgeLabel = resolvePreferredEdgeLabel({
			runtimeSourceName: options.runtimeSourceName,
			persistedSourceDisplayLabel: options.sourceDisplayLabel,
		});
		this.name = this.edgeLabel;
	}

	/** @returns 关系面板使用的稳定显示名。 */
	public override get baseDisplayName(): string {
		return this.edgeLabel;
	}

	/** @returns 与当前投影载荷相互独立的新关系实例。 */
	public override cloneSelf(): ReferenceDrivenArcOnCurveConstraint {
		return new ReferenceDrivenArcOnCurveConstraint({
			referenceId: this.referenceId,
			projectedCenterWorld: this.projectedCenterWorld.clone(),
			projectedRadius: this.projectedRadius,
		});
	}

	/**
	 * 创建统一参数方程使用的局部基础圆载荷。
	 *
	 * @returns 目标草图参考面局部坐标中的稳定基础圆；目标尚未绑定时返回 `null`。
	 */
	public override createParametricFormulaPayload() {
		const target = this.getTargetOrThrow();
		if (!target) {
			return null;
		}

		// 外部引用记录保存世界坐标，公式系统只接收草图平面局部坐标。
		const center = target.referencePlane.worldToLocal(this.projectedCenterWorld);
		return {
			kind: 'external-circle',
			referenceId: this.referenceId,
			center: [center.x, center.y],
			radius: this.projectedRadius,
		};
	}
}
